import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthTheme, getAuthStyles, injectAuthKeyframes } from './AuthTheme';

injectAuthKeyframes();

const PARTICLES = [
  { top: '10%', left: '8%', animationDelay: '0s' },
  { top: '20%', right: '10%', animationDelay: '1s' },
  { top: '60%', left: '5%', animationDelay: '2s' },
  { bottom: '20%', right: '8%', animationDelay: '0.5s' },
  { bottom: '10%', left: '20%', animationDelay: '1.5s' },
  { top: '45%', right: '5%', animationDelay: '2.5s' },
];

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { dark } = useAuthTheme();
  const s = getAuthStyles();

  const reset = () => { setStep(1); setEmail(''); setAnswer(''); setNewPassword(''); setConfirmPassword(''); setError(''); setMessage(''); };

  const handleEmail = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/forgot-password/check-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) { setSecurityQuestion(data.question); setStep(2); }
      else setError(data.error || 'Email not found');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleAnswer = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    if (!answer.trim()) { setError('Please provide your answer'); setLoading(false); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/forgot-password/verify-answer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, answer }),
      });
      const data = await res.json();
      if (res.ok) { setToken(data.token); setStep(3); }
      else setError(data.error || 'Incorrect answer');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault(); setError(''); setMessage(''); setLoading(true);
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) { setMessage('Password reset! Redirecting to login...'); setTimeout(() => navigate('/login'), 2500); }
      else setError(data.error || 'Failed to reset password');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const stepTitles = ['', 'Forgot Password?', 'Security Question', 'Reset Password'];
  const stepSubs   = ['', 'Enter your email to get started', 'Answer your security question', 'Create a new password'];

  return (
    <div style={s.page}>
      <div style={s.orb1} /><div style={s.orb2} /><div style={s.orb3} />
      <div style={s.particles}>
        {PARTICLES.map((p, i) => <div key={i} style={{ ...s.particle, ...p }} />)}
      </div>

      <div style={s.wrap}>
        <div style={s.brand} onClick={() => navigate('/')}>
          <div style={s.logoRing}><span style={s.logoEmoji}>🎓</span><div style={s.logoGlow} /></div>
          <h1 style={s.brandName}>UniResolve</h1>
          <p style={s.brandSub}>Password Recovery</p>
        </div>

        <div style={s.card}>
          <div style={s.cardTop}>
            <div style={{ flex: 1 }}>
              <div style={s.cardTopBadge}>{stepTitles[step]}</div>
            </div>
            {/* step dots */}
            <div style={{ display: 'flex', gap: 6 }}>
              {[1,2,3].map(n => (
                <div key={n} style={{ width: 8, height: 8, borderRadius: '50%', transition: 'background 0.3s',
                  background: step >= n ? '#a5b4fc' : 'rgba(255,255,255,0.25)' }} />
              ))}
            </div>
          </div>
          <div style={s.cardBody}>
            <h2 style={s.heading}>{stepTitles[step]}</h2>
            <p style={s.sub}>{stepSubs[step]}</p>

            {error && <div style={s.err}><span>{error}</span><button type="button" onClick={() => setError('')} style={s.errX}>x</button></div>}
            {message && <div style={s.ok}><span>{message}</span></div>}

            {step === 1 && (
              <form onSubmit={handleEmail}>
                <div style={s.field}>
                  <label style={s.label}>Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your university email" style={s.input} required />
                </div>
                <button type="submit" disabled={loading} style={{ ...s.btn, ...(loading ? s.btnOff : {}) }}>
                  {loading ? <span style={s.btnIn}><span style={s.spin} /> Checking...</span> : 'Continue'}
                </button>
                <div style={s.regRow}>
                  <span style={s.regLink} onClick={() => navigate('/login')}>Back to Login</span>
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleAnswer}>
                <div style={s.questionBox}>
                  <p style={s.questionText}>{securityQuestion}</p>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Your Answer</label>
                  <input type="text" value={answer} onChange={e => setAnswer(e.target.value)}
                    placeholder="Enter your answer" style={s.input} required />
                </div>
                <button type="submit" disabled={loading} style={{ ...s.btn, ...(loading ? s.btnOff : {}) }}>
                  {loading ? <span style={s.btnIn}><span style={s.spin} /> Verifying...</span> : 'Verify Answer'}
                </button>
                <div style={s.regRow}>
                  <span style={s.regLink} onClick={reset}>Try Different Email</span>
                </div>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleReset}>
                <div style={s.field}>
                  <label style={s.label}>New Password</label>
                  <div style={s.pwWrap}>
                    <input type={showPw ? 'text' : 'password'} value={newPassword}
                      onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" style={s.input} required />
                    <button type="button" onClick={() => setShowPw(!showPw)} style={s.pwBtn}>{showPw ? 'Hide' : 'Show'}</button>
                  </div>
                  <span style={s.hint}>Minimum 6 characters</span>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Confirm Password</label>
                  <div style={s.pwWrap}>
                    <input type={showCpw ? 'text' : 'password'} value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" style={s.input} required />
                    <button type="button" onClick={() => setShowCpw(!showCpw)} style={s.pwBtn}>{showCpw ? 'Hide' : 'Show'}</button>
                  </div>
                </div>
                <button type="submit" disabled={loading} style={{ ...s.btn, ...(loading ? s.btnOff : {}) }}>
                  {loading ? <span style={s.btnIn}><span style={s.spin} /> Resetting...</span> : 'Reset Password'}
                </button>
                <div style={s.regRow}>
                  <span style={s.regLink} onClick={reset}>Start Over</span>
                </div>
              </form>
            )}
          </div>
        </div>
        <p style={s.footer}>2026 UniResolve</p>
      </div>
    </div>
  );
}
